/* BranchesCard — admin-only Branches CRUD card injected into Settings.
 * Powered by /api/branches (Phase A → branches-as-data, Phase B → admin UI). */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, PencilSimple, Trash, Buildings } from '@phosphor-icons/react';
import { toast } from 'sonner';

const EMPTY = { name: '', code: '', address: '', phone: '', active: true };

export default function BranchesCard() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);          // null | branch object
  const [form, setForm] = useState(EMPTY);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/branches').then(r => setRows(r.data || [])).catch(() => {});

  useEffect(() => { load(); }, []);

  if (!isAdmin) return null;

  const open = (b) => {
    setEditing(b || null);
    setForm(b ? { name: b.name, code: b.code || '', address: b.address || '', phone: b.phone || '', active: !!b.active } : EMPTY);
    setDialogOpen(true);
  };

  const close = () => {
    setDialogOpen(false);
    setEditing(null);
    setForm(EMPTY);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Branch name is required'); return; }
    setBusy(true);
    try {
      if (editing) await api.put(`/branches/${editing.id}`, form);
      else await api.post('/branches', form);
      toast.success(editing ? 'Branch updated' : 'Branch created');
      close();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (b) => {
    if (b.is_main) { toast.error('Cannot delete the main branch'); return; }
    if (!window.confirm(`Delete branch "${b.name}"?`)) return;
    try {
      await api.delete(`/branches/${b.id}`);
      toast.success('Branch deleted');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <Card className="rounded-sm border-[#E4E4E7] shadow-none" data-testid="branches-card">
      <CardHeader className="p-5 pb-3 flex-row items-center justify-between">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Buildings size={18} className="text-[#3B82F6]" /> Branches
        </CardTitle>
        <Button size="sm" className="rounded-sm bg-[#0A0A0A] text-white hover:bg-black/80 hover:text-white" onClick={() => open(null)} data-testid="add-branch-btn">
          <Plus size={14} className="mr-1.5" /> Add Branch
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {rows.length === 0 ? (
          <p className="p-6 text-sm text-[#A1A1AA] text-center">No branches yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[#E4E4E7]">
                  {['Name', 'Code', 'Phone', 'Address', 'Status', 'Actions'].map(h => (
                    <TableHead key={h} className="text-[10px] uppercase tracking-[0.1em] font-semibold">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(b => (
                  <TableRow key={b.id} className="border-[#E4E4E7]" data-testid={`branch-row-${b.id}`}>
                    <TableCell className="text-sm font-medium">
                      {b.name}
                      {b.is_main && <Badge className="ml-2 rounded-md text-[10px] bg-[#DBEAFE] text-[#1D4ED8] border-[#BFDBFE]" variant="outline">MAIN</Badge>}
                    </TableCell>
                    <TableCell className="text-xs font-mono text-[#52525B]">{b.code || '—'}</TableCell>
                    <TableCell className="text-xs text-[#52525B]">{b.phone || '—'}</TableCell>
                    <TableCell className="text-xs text-[#52525B] max-w-[260px] truncate" title={b.address}>{b.address || '—'}</TableCell>
                    <TableCell>
                      <Badge className={`rounded-md text-[10px] ${b.active ? 'bg-[#DCFCE7] text-[#15803D] border-[#86EFAC]' : 'bg-[#F4F4F5] text-[#52525B] border-[#E4E4E7]'}`} variant="outline">
                        {b.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => open(b)} data-testid={`edit-branch-${b.id}`}><PencilSimple size={14} /></Button>
                        {!b.is_main && (
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-[#EF4444]" onClick={() => remove(b)} data-testid={`delete-branch-${b.id}`}><Trash size={14} /></Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={(v) => { if (!v) close(); }}>
        <DialogContent className="rounded-xl border-[#E4E4E7] max-w-md" data-testid="branch-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Branch' : 'Add Branch'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="Branch Name *"><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="rounded-lg" data-testid="branch-name" /></Field>
            <Field label="Code (e.g. MAIN, BR2)"><Input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} className="rounded-lg font-mono" data-testid="branch-code" /></Field>
            <Field label="Phone"><Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="rounded-lg" data-testid="branch-phone" /></Field>
            <Field label="Address"><Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} className="rounded-lg" data-testid="branch-address" /></Field>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} data-testid="branch-active" />
              <span>Active</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={close}>Cancel</Button>
            <Button className="bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-xl" onClick={save} disabled={busy} data-testid="save-branch-btn">
              {busy ? 'Saving…' : (editing ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
